// D-C ("click the type tag, should show the top recommendations and a
// input box to search/add new tag"): a compact popover anchored to the
// type pill -- never a modal, never a long dropdown list. Sections, in
// order:
//
//   1. "Suggested" -- up to 5 real recommendations from
//      `useTypeSuggestions` (D-D). Loading -> skeleton rows (never a
//      guessed name); error -> the section is hidden entirely and search
//      still works (AGENTS.md Data & UI honesty: an honest gap, not a
//      fabricated fallback).
//   2. A search input (cmdk `Command`) filtering leaves by name.
//   3. "Add new type..." -- creates a custom leaf via `onCreateCategory`
//      (`useCreateCategory`, D6) and applies it.
//
// DES-R1-001/002 + ENG-R1-002 (design/engineering round 1): the compact
// SCOPED mode (`scope: 'personal' | 'business'`, restricted to that
// scope+kind's leaves) is only reachable when the item's scope+kind is
// resolvable. Every other case -- a transfer-scoped item, an uncategorized
// item, or one whose `category_id` has no row in `useCategories` -- uses
// the scope-AGNOSTIC `scope: 'any'` mode instead: ALL leaves, grouped by
// their five parents (Personal expense, Personal income, Business expense,
// Business income, Transfer), Suggested calls the endpoint with
// `scope=any`, search spans every parent, and "Add new type" offers a
// small parent select (defaulting to `currentParentId` when known) instead
// of a fixed parent -- DES-R1-004: the row always states which parent the
// new leaf lands under. Choosing a leaf in a different scope than the
// item's current one is a normal PATCH; the server derives `is_business`.
//
// Choosing any item calls `onSelect`/`onCreateCategory` and closes the
// popover; the caller (FeedRow/TransactionDetailPanel) owns the actual
// PATCH. Escape/outside click closes via Radix Popover.
//
// The popover is CONTROLLED (`open`/`onOpenChange`) rather than owning its
// own trigger: `anchor` is the already-rendered type pill (`CategoryChip`,
// whose own `onOpenTypeChooser` callback flips `open` true) wrapped in
// `PopoverAnchor` so the popover positions against that pill specifically,
// not an internal trigger button of its own -- there is exactly one click
// target, the pill CategoryChip already renders.
import { useEffect, useRef, useState, type RefObject } from "react";
import {
  Checkbox,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Sheet,
  SheetContent,
  Skeleton,
} from "@moneyskyline/ui";
import type { Category } from "@/api/hooks/useCategories";
import { useTypeSuggestions } from "@/api/hooks/useTypeSuggestions";
import { useShellContext } from "@/app/shell/AppShell";
import {
  emitTypeChooserAdd,
  emitTypeChooserOpen,
  emitTypeChooserSearchDebounced,
  newInteractionId,
  queryLengthBucket,
  viewportBandFromShellBand,
  type TelemetryContext,
  type Surface,
} from "./telemetry";

export type TypeChooserSelection = "suggested" | "search" | "browse";

/** §8: the stable id `aria-controls` on the type button must reference -- shared with the trigger caller (FeedRow/TransactionDetailPanel) so both sides agree without threading a ref. */
export function typeChooserContentId(lineItemId: string): string {
  return `type-chooser-content-${lineItemId}`;
}

// Fixed display order for the any-mode's five system parents -- matches
// `backend/data/category_taxonomy.v1.json`'s parent order.
const ANY_MODE_PARENT_SLUGS = [
  "personal_expense",
  "personal_income",
  "business_expense",
  "business_income",
  "transfer",
] as const;

export function TypeChooser({
  anchorRef,
  open,
  onOpenChange,
  lineItemId,
  scope,
  kind,
  categories,
  currentCategoryId,
  currentParentId,
  onSelect,
  onCreateCategory,
  busy,
  creating,
  createError,
  surface,
  entry,
  merchantName,
  applyToMerchantChecked,
  onApplyToMerchantChange,
  merchantApplyCount,
}: {
  /**
   * A ref to the ALREADY-RENDERED real type button (e.g. `CategoryChip`'s
   * forwarded type-tag ref) -- spec §4.3: "Anchor to the actual type
   * button, not the enclosing CategoryChip group." Uses Radix Popper's
   * `virtualRef` so this popover positions against that exact element
   * without re-wrapping/cloning it (no second click target, no anchoring to
   * the scope tag or the group).
   */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lineItemId: string;
  /** `'any'` is the scope-agnostic mode (DES-R1-001/002) -- used whenever the item's scope+kind can't be resolved to personal/business (transfer, uncategorized, unknown category_id). */
  scope: "personal" | "business" | "any";
  /** Required for `scope: 'personal' | 'business'`; ignored for `'any'`. */
  kind?: "expense" | "income";
  /** The full taxonomy (system + this user's custom leaves). */
  categories: Category[];
  currentCategoryId: string | null;
  /** The item's current parent (when known) -- any-mode's "Add new type" defaults to it. */
  currentParentId?: string | null;
  onSelect: (categoryId: string, selection: TypeChooserSelection) => void;
  onCreateCategory: (parentId: string, name: string) => void;
  busy?: boolean;
  /** ENG-R1-006: the "Add new type" POST is in flight -- keeps the popover open with the search/group intact and shows "Adding type…" instead of closing immediately. */
  creating?: boolean;
  /** ENG-R1-006: the create POST definitively failed -- shown next to the still-editable "Add new type" row so the name/group are never discarded. */
  createError?: string | null;
  /** §9: the caller's surface -- `'feed' | 'detail' | 'inbox'`. `input_method` is not reliably known at this call site, so this always reports `'unknown'` (never guessed). */
  surface: Surface;
  /** §9 `transactions_type_chooser_open`'s `entry` -- the item's category state when the chooser was opened. */
  entry: "categorized" | "uncategorized" | "unknown" | "transfer";
  /** §14 decision 3: the same "Apply to all items from {merchant}" checkbox offered by the scope menu, shown in this chooser's footer so a type change can spread to the merchant in one interaction. Omitted entirely (no checkbox rendered) when the caller hasn't wired the merchant-apply flow. */
  merchantName?: string | null;
  applyToMerchantChecked?: boolean;
  onApplyToMerchantChange?: (checked: boolean) => void;
  /** The real all-time eligible count from a PRIOR write on this item -- `null`/`undefined` shows a count-free label. */
  merchantApplyCount?: number | null;
}) {
  // §4.3/§8: Popover on tablet/desktop (>=720px), the existing Sheet bottom
  // variant on phone (<720px) -- same chooser body either way.
  const { band } = useShellContext();
  const isMobile = band === "mobile";
  const telemetryContext: TelemetryContext = {
    surface,
    viewport_band: viewportBandFromShellBand(band),
    input_method: "unknown",
  };
  const [search, setSearch] = useState("");
  const [addParentId, setAddParentId] = useState<string | null>(null);
  // §4.3: scoped mode's "Browse other groups" footer switches into
  // all-parent mode WITHIN THE SAME chooser (no separate editor) so a
  // scoped item can still be corrected into Transfer or the other kind.
  const [browseAny, setBrowseAny] = useState(false);
  // §4.3: "On empty search, show only Suggested and 'All types'; activating
  // All types shows loaded leaves ... This keeps recommendations compact
  // without a dead end when none exist."
  const [browseAllTypes, setBrowseAllTypes] = useState(false);
  // Reset the browse-mode toggles when `open` transitions to closed --
  // during RENDER (React's documented "adjusting state when a prop
  // changes" pattern), not inside a `useEffect` body, which the repo's
  // `react-hooks/set-state-in-effect` lint rule (rightly) flags as a
  // cascading-render risk for direct setState-in-effect.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setBrowseAny(false);
      setBrowseAllTypes(false);
      // ENG-R1-006: a successful create closes the popover via the `open`
      // prop going false from the CALLER (not a user click inside this
      // component, which already goes through `closeAndReset`) -- clear
      // the pending search/group here too so a later reopen starts fresh.
      setSearch("");
      setAddParentId(null);
    }
  }
  const currentCategory = currentCategoryId
    ? categories.find((c) => c.category_id === currentCategoryId)
    : undefined;

  // DES-R1-003/TEST-R1-001: Radix's automatic "return focus to the trigger
  // on close" only wires up for `Popover.Trigger`; this popover anchors to
  // the already-rendered type pill via `PopoverAnchor` instead (D-C: one
  // click target, no second trigger element), which Radix does NOT treat
  // as a focus-return target. Track what had focus when the popover opened
  // (the type pill, since a click focuses the element it lands on) and
  // restore it on every close -- Escape, outside click, or a real
  // selection -- so keyboard users never lose their place.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    } else {
      // Restore only when focus was lost to the document (Escape, selection,
      // click on non-focusable background) or is still inside the closing
      // popover. If the user clicked INTO another control, leave it there --
      // Radix's non-modal popover deliberately does not yank focus back
      // (Engineering R2, non-blocking 1).
      const active = document.activeElement;
      const insidePopover =
        active instanceof HTMLElement &&
        !!active.closest('[data-testid="type-chooser"]');
      if (active === null || active === document.body || insidePopover) {
        previouslyFocusedRef.current?.focus();
      }
    }
  }, [open]);

  // §9: one opaque session id per open, shared by the debounced search event
  // and the add-flow's own stages -- never the raw query. Regenerated each
  // time the popover transitions to open (not on every render).
  const sessionIdRef = useRef<string>(newInteractionId());
  const addOpenEmittedRef = useRef(false);
  useEffect(() => {
    if (open) {
      sessionIdRef.current = newInteractionId();
      addOpenEmittedRef.current = false;
      emitTypeChooserOpen(telemetryContext, newInteractionId(), {
        line_item_id: lineItemId,
        mode: scope === "any" ? "any" : "scoped",
        parent_id: currentParentId ?? null,
        presentation: isMobile ? "sheet" : "popover",
        entry,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per open transition only; re-running on every prop change would over-emit.
  }, [open]);

  // §8: "aria-controls" on the trigger needs a stable id for this chooser's
  // own content -- one per line item, not regenerated on every render.
  const contentId = typeChooserContentId(lineItemId);

  const isAnyMode = scope === "any" || browseAny;

  const suggestionsQuery = useTypeSuggestions(lineItemId, {
    scope: isAnyMode ? "any" : scope,
    enabled: open,
  });
  const suggestions = (suggestionsQuery.data?.suggestions ?? []).filter(
    (s) => s.category_id !== currentCategoryId,
  );
  // ENG-R1-001/TEST-R1-001/DES-R1-001: results are always deduped against
  // the suggestion set -- Suggested itself is additionally hidden outright
  // on a nonempty search (`showSuggested` below), but the empty-search "All
  // types" browse view can render alongside Suggested, so the dedupe has to
  // apply there too or the same leaf shows twice.
  const suggestionIds = new Set(suggestions.map((s) => s.category_id));

  // -- Scoped mode: leaves restricted to the current scope+kind's system parent.
  const scopedParent = !isAnyMode
    ? categories.find(
        (c) =>
          c.parent_id === null &&
          c.kind === kind &&
          c.is_business === (scope === "business"),
      )
    : undefined;
  const scopedLeaves = !isAnyMode
    ? categories.filter(
        (c) =>
          c.parent_id !== null &&
          c.kind === kind &&
          c.is_business === (scope === "business") &&
          c.category_id !== currentCategoryId,
      )
    : [];

  // -- Any mode: the five system parents, in fixed order, each with its leaves.
  const anyModeParents = isAnyMode
    ? ANY_MODE_PARENT_SLUGS.map((slug) =>
        categories.find((c) => c.parent_id === null && c.slug === slug),
      ).filter((c): c is Category => !!c)
    : [];
  const anyModeLeavesByParent = anyModeParents.map((parent) => ({
    parent,
    leaves: categories.filter(
      (c) =>
        c.parent_id === parent.category_id &&
        c.category_id !== currentCategoryId,
    ),
  }));

  const trimmedSearch = search.trim();
  // §4.3: nonempty search always shows results (replacing the browse view);
  // empty search shows results only after "All types" was activated.
  const showResults = trimmedSearch.length > 0 || browseAllTypes;

  // DES-R1-001/ENG-R1-001/TEST-R1-001: results are deduped against
  // Suggested only while Suggested is ACTUALLY showing (empty-search "All
  // types" browse). A nonempty search hides Suggested outright, so a
  // suggested-and-matching leaf must still appear in the one results list
  // -- deduping unconditionally would drop it from BOTH.
  const dedupeAgainstSuggestions = !trimmedSearch;

  const filteredScopedLeaves =
    !isAnyMode && showResults
      ? scopedLeaves
          .filter((leaf) => !dedupeAgainstSuggestions || !suggestionIds.has(leaf.category_id))
          .filter((leaf) =>
            trimmedSearch
              ? leaf.name.toLowerCase().includes(trimmedSearch.toLowerCase())
              : true,
          )
      : [];

  const anyModeSearchGroups =
    isAnyMode && showResults
      ? anyModeLeavesByParent
          .map(({ parent, leaves }) => ({
            parent,
            leaves: leaves
              .filter((leaf) => !dedupeAgainstSuggestions || !suggestionIds.has(leaf.category_id))
              .filter((leaf) =>
                trimmedSearch
                  ? leaf.name.toLowerCase().includes(trimmedSearch.toLowerCase())
                  : true,
              ),
          }))
          .filter((group) => group.leaves.length > 0)
      : [];

  const allFilteredLeaves = isAnyMode
    ? anyModeSearchGroups.flatMap((group) => group.leaves)
    : filteredScopedLeaves;
  const hasExactMatch = allFilteredLeaves.some(
    (leaf) => leaf.name.toLowerCase() === trimmedSearch.toLowerCase(),
  );
  const searchMatchesCurrent =
    !!trimmedSearch &&
    !!currentCategory &&
    currentCategory.name.toLowerCase() === trimmedSearch.toLowerCase();

  // ENG-R1-006: never invent a default group -- in any-mode, with no
  // explicit choice and no known current parent, this is `undefined` until
  // the user picks one from the "Add as" select (previously fell back to
  // `anyModeParents[0]`, silently defaulting a new leaf into "Personal
  // expense" the user never chose).
  const effectiveAddParent = isAnyMode
    ? categories.find(
        (c) => c.category_id === (addParentId ?? currentParentId ?? undefined),
      )
    : scopedParent;

  const canAddNew = trimmedSearch.length > 0 && !hasExactMatch && !searchMatchesCurrent && !!effectiveAddParent;

  // §9 search: debounced (telemetry.ts owns the 300ms/dedupe), never the raw
  // query string -- only its length (bucketed downstream) and result count.
  useEffect(() => {
    if (!open) return;
    emitTypeChooserSearchDebounced(telemetryContext, newInteractionId(), {
      chooser_session_id: sessionIdRef.current,
      query_length: search.length,
      result_count: showResults ? allFilteredLeaves.length : null,
      taxonomy_status: "ready",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- telemetryContext is a fresh object per render; only `search`/result count/open matter for when to (re)debounce.
  }, [open, search, showResults, allFilteredLeaves.length]);

  // §9 add: 'open' stage the first time this search session's "Add new
  // type" affordance becomes visible -- once per chooser session, never
  // re-fired on every keystroke.
  useEffect(() => {
    if (!open || !canAddNew || addOpenEmittedRef.current) return;
    addOpenEmittedRef.current = true;
    emitTypeChooserAdd(telemetryContext, newInteractionId(), {
      stage: "open",
      parent_id: effectiveAddParent?.category_id ?? null,
      name_length_bucket: queryLengthBucket(trimmedSearch.length),
      error_class: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- telemetryContext/effectiveAddParent are fresh per render; only the open/canAddNew transition matters.
  }, [open, canAddNew]);

  function closeAndReset() {
    onOpenChange(false);
    setSearch("");
    setAddParentId(null);
  }

  function handleSelect(categoryId: string, selection: TypeChooserSelection) {
    onSelect(categoryId, selection);
    closeAndReset();
  }

  function handleAddNew() {
    if (!trimmedSearch || !effectiveAddParent || creating) return;
    // ENG-R1-006: does NOT close the popover -- the POST is async, and a
    // failure must keep the name/group on screen ("Adding type…" while
    // pending, `createError` on failure) rather than discard them. The
    // caller closes this popover itself (via the `open` prop) once the
    // category is actually created (`useCategoryCorrection.createAndApply`'s
    // `onCreated`), independent of whether the follow-up apply succeeds.
    onCreateCategory(effectiveAddParent.category_id, trimmedSearch);
  }

  // §9: the search box has content ('search') vs. an empty query resolved via "All types" ('browse').
  const resultSelection: TypeChooserSelection = trimmedSearch ? "search" : "browse";

  // DES-R1-001/ENG-R1-001/TEST-R1-001: a nonempty search REPLACES Suggested
  // with the one results list (spec §4.3) -- never render both. Suggested
  // can still legitimately coexist with the empty-search "All types" browse
  // view (`browseAllTypes` with no search text); the dedupe above keeps
  // that combination from repeating a leaf.
  const showSuggested = !trimmedSearch;

  const heading = isAnyMode ? "Categorize transaction" : "Change type";

  const chooserBody = (
    <>
      <div className="border-b border-muted-foreground/30 px-3 py-2 text-sm font-medium text-foreground">
        {heading}
      </div>
      <Command shouldFilter={false} className="flex min-h-0 flex-1 flex-col">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search or add a type…"
            aria-label="Search types"
            data-testid="type-chooser-search"
            autoFocus
          />
          <CommandList className="flex-1 overflow-y-auto">
            {showSuggested && suggestionsQuery.isLoading && (
              <div
                className="space-y-1 p-2"
                data-testid="type-chooser-suggestions-loading"
              >
                <div className="px-1 pb-1 text-xs font-medium text-muted-foreground">
                  Suggested
                </div>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-8 w-full rounded-md" />
                ))}
              </div>
            )}
            {/* D1 (engineering deferrable): Suggested lives INSIDE
                CommandList so it participates in cmdk's arrow-key/aria
                listbox navigation, not as a sibling before the input.
                DES-R1-001/ENG-R1-001/TEST-R1-001: gated on `showSuggested`
                -- a nonempty search replaces this whole section with the
                one results list below, never rendering both. */}
            {showSuggested &&
              !suggestionsQuery.isLoading &&
              !suggestionsQuery.isError &&
              suggestions.length > 0 && (
                <CommandGroup
                  heading="Suggested"
                  data-testid="type-chooser-suggestions"
                >
                  {suggestions.map((suggestion) => (
                    <CommandItem
                      key={suggestion.category_id}
                      data-testid="type-chooser-suggestion"
                      data-category-id={suggestion.category_id}
                      onSelect={() => handleSelect(suggestion.category_id, "suggested")}
                      disabled={busy}
                    >
                      {suggestion.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            {showSuggested &&
              !suggestionsQuery.isLoading &&
              !suggestionsQuery.isError &&
              suggestions.length === 0 && (
                <div
                  className="px-3 py-2 text-xs text-muted-foreground"
                  data-testid="type-chooser-suggestions-empty"
                >
                  No suggestions yet. Search or browse all types.
                </div>
              )}
            {showSuggested && suggestionsQuery.isError && (
              <div
                className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-muted-foreground"
                data-testid="type-chooser-suggestions-failed"
              >
                <span>Suggestions unavailable. You can still search.</span>
                <button
                  type="button"
                  className="ms-tap-44 shrink-0 underline"
                  onClick={() => suggestionsQuery.refetch()}
                >
                  Retry
                </button>
              </div>
            )}

            {currentCategory && (
              <div
                className="px-3 py-1 text-xs text-muted-foreground"
                data-testid="type-chooser-current"
              >
                Current: {currentCategory.name}
              </div>
            )}

            {trimmedSearch && searchMatchesCurrent && (
              <div
                className="px-3 py-2 text-xs text-muted-foreground"
                data-testid="type-chooser-already-selected"
              >
                Already selected
              </div>
            )}

            {showResults && !isAnyMode && (
              <CommandGroup
                heading="All types"
                data-testid="type-chooser-results"
              >
                {filteredScopedLeaves.length === 0 ? (
                  // D2 (engineering deferrable): a plain element, not cmdk's
                  // own `CommandEmpty` -- with `shouldFilter={false}` cmdk
                  // never tracks a "no results" state itself, so that
                  // component's own conditional never fires; this renders
                  // off the SAME `filteredLeaves.length === 0` check the
                  // "Add new type" row below already uses.
                  <div
                    className="px-2 py-3 text-xs text-muted-foreground"
                    data-testid="type-chooser-empty"
                  >
                    No matching type.
                  </div>
                ) : (
                  filteredScopedLeaves.map((leaf) => (
                    <CommandItem
                      key={leaf.category_id}
                      data-testid="type-chooser-option"
                      data-category-id={leaf.category_id}
                      onSelect={() => handleSelect(leaf.category_id, resultSelection)}
                      disabled={busy}
                    >
                      {leaf.name}
                    </CommandItem>
                  ))
                )}
                {trimmedSearch && !hasExactMatch && !searchMatchesCurrent && scopedParent && (
                  <CommandItem
                    data-testid="type-chooser-add-new"
                    onSelect={handleAddNew}
                    disabled={busy || creating}
                  >
                    {creating
                      ? "Adding type…"
                      : `Add “${trimmedSearch}” as a ${scopedParent.name} type…`}
                  </CommandItem>
                )}
                {createError && (
                  <p
                    className="px-2 py-1 text-xs text-destructive"
                    role="alert"
                    data-testid="type-chooser-create-error"
                  >
                    {createError}
                  </p>
                )}
              </CommandGroup>
            )}

            {showResults && isAnyMode && (
              <div data-testid="type-chooser-results">
                {anyModeSearchGroups.length === 0 ? (
                  <div
                    className="px-2 py-3 text-xs text-muted-foreground"
                    data-testid="type-chooser-empty"
                  >
                    No matching type.
                  </div>
                ) : (
                  anyModeSearchGroups.map(({ parent, leaves }) => (
                    <CommandGroup
                      key={parent.category_id}
                      heading={parent.name}
                      data-testid="type-chooser-group"
                    >
                      {leaves.map((leaf) => (
                        <CommandItem
                          key={leaf.category_id}
                          data-testid="type-chooser-option"
                          data-category-id={leaf.category_id}
                          onSelect={() => handleSelect(leaf.category_id, resultSelection)}
                          disabled={busy}
                        >
                          {leaf.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))
                )}
                {trimmedSearch && !hasExactMatch && !searchMatchesCurrent && (
                  <CommandItem
                    data-testid="type-chooser-add-new"
                    onSelect={handleAddNew}
                    disabled={busy || creating || !effectiveAddParent}
                  >
                    {creating
                      ? "Adding type…"
                      : effectiveAddParent
                        ? `Add “${trimmedSearch}” as a ${effectiveAddParent.name} type…`
                        : `Add “${trimmedSearch}”… (choose a group)`}
                  </CommandItem>
                )}
                {createError && (
                  <p
                    className="px-2 py-1 text-xs text-destructive"
                    role="alert"
                    data-testid="type-chooser-create-error"
                  >
                    {createError}
                  </p>
                )}
              </div>
            )}
          </CommandList>
      </Command>
      {/* §8: "Keep native Group select and buttons outside the listbox" --
          ENG-R1 deferrable. The any-mode "Add as" parent select and the
          browse toggles are plain, non-cmdk controls in a fixed footer
          below the scrolling `CommandList`, so ArrowDown/Up (cmdk's own
          roving-item navigation, scoped to `[cmdk-item]` elements inside
          the list) never lands on them, and a screen reader's listbox
          never announces a `<select>`/plain button as one of its options. */}
      {(isAnyMode && trimmedSearch && !hasExactMatch && !searchMatchesCurrent) ||
      (!trimmedSearch && !browseAllTypes) ||
      !isAnyMode ||
      onApplyToMerchantChange ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border px-3 py-2"
          data-testid="type-chooser-footer"
        >
          {/* §14 decision 3: the same "Apply to all items from {merchant}" checkbox the scope menu offers -- lets a type change spread to the merchant in one interaction.
              DATA-F-001: the headline never carries a count -- `N` is only
              the ELIGIBLE count (excludes items the user already set
              themselves and the item just corrected), so "all N" overstates
              it. A known count instead renders a second, honest line. */}
          {onApplyToMerchantChange && (
            <label className="ms-tap-44 flex min-h-11 items-center gap-1.5 text-xs text-foreground">
              <Checkbox
                checked={!!applyToMerchantChecked}
                onCheckedChange={(checked) => onApplyToMerchantChange(checked === true)}
                data-testid="type-chooser-apply-to-merchant"
              />
              <span className="flex flex-col">
                <span>
                  {merchantName
                    ? `Apply to all items from ${merchantName}`
                    : "Apply to all items from this merchant"}
                </span>
                {merchantApplyCount != null && (
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid="type-chooser-apply-to-merchant-eligible"
                  >
                    {merchantApplyCount} eligible · items you set yourself stay as they are
                  </span>
                )}
              </span>
            </label>
          )}
          {isAnyMode && trimmedSearch && !hasExactMatch && !searchMatchesCurrent && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <label htmlFor="type-chooser-add-new-parent">Add as</label>
              <select
                id="type-chooser-add-new-parent"
                data-testid="type-chooser-add-new-parent"
                value={effectiveAddParent?.category_id ?? ""}
                onChange={(event) => setAddParentId(event.target.value || null)}
                className="ms-tap-44 rounded border border-border bg-transparent px-1 py-0.5 text-xs text-foreground"
              >
                {/* ENG-R1-006: no invented default group -- placeholder stays selected until the user picks one. */}
                <option value="" disabled>
                  Choose a group
                </option>
                {anyModeParents.map((parent) => (
                  <option key={parent.category_id} value={parent.category_id}>
                    {parent.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!trimmedSearch && !browseAllTypes && (
            <button
              type="button"
              data-testid="type-chooser-browse-all"
              className="ms-tap-44 text-xs text-link underline"
              onClick={() => setBrowseAllTypes(true)}
            >
              All types
            </button>
          )}
          {!isAnyMode && (
            <button
              type="button"
              data-testid="type-chooser-browse-other-groups"
              className="ms-tap-44 text-xs text-link underline"
              onClick={() => setBrowseAny(true)}
            >
              Browse other groups
            </button>
          )}
        </div>
      ) : null}
    </>
  );

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setSearch("");
      setAddParentId(null);
    }
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          id={contentId}
          side="bottom"
          data-testid="type-chooser"
          role="dialog"
          aria-label={heading}
          className="flex max-h-[85dvh] flex-col gap-0 p-0"
        >
          {chooserBody}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        id={contentId}
        data-testid="type-chooser"
        role="dialog"
        aria-label={heading}
        className="z-50 flex max-h-[min(480px,calc(100vh-24px))] w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-hidden p-0"
      >
        {chooserBody}
      </PopoverContent>
    </Popover>
  );
}

export default TypeChooser;
